import React, { useState, useCallback, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { X, Scissors, Crop, SkipForward, Check, ArrowLeft, Play, Pause, Loader2, Hand } from 'lucide-react';
import { mediaAPI } from '../../../services/api';

// ─── Default Constants ──────────────────────────────────────────────
const DEFAULT_TARGET_WIDTH = 1080;
const DEFAULT_TARGET_HEIGHT = 1080;
const DEFAULT_MAX_TRIM = 10; // seconds
const MIN_CROP_PCT = 5;      // minimum crop size in %

// ─── MediaCropTrimModal ─────────────────────────────────────────────
const MediaCropTrimModal = forwardRef((props, ref) => {
    // ── State ────────────────────────────────────────────────────────
    const [isOpen, setIsOpen] = useState(false);
    const [mediaType, setMediaType] = useState('image');
    const [mediaUrl, setMediaUrl] = useState(null);
    const [onConfirmCallback, setOnConfirmCallback] = useState(null);

    // Target dimensions from nexjob asset (or defaults)
    const [targetWidth, setTargetWidth] = useState(DEFAULT_TARGET_WIDTH);
    const [targetHeight, setTargetHeight] = useState(DEFAULT_TARGET_HEIGHT);
    const [maxTrimDuration, setMaxTrimDuration] = useState(DEFAULT_MAX_TRIM);
    // Stock thumbnail URL passed from the media selector (used as poster on skip)
    const [stockThumbnailUrl, setStockThumbnailUrl] = useState(null);

    // Crop state (percentages 0–100 of the media element)
    const [crop, setCrop] = useState({ x: 0, y: 0, width: 100, height: 100 });

    // Trim state (seconds)
    const [trimStart, setTrimStart] = useState(0);
    const [trimEnd, setTrimEnd] = useState(DEFAULT_MAX_TRIM);
    const [videoDuration, setVideoDuration] = useState(0);

    // UI state
    const [isDragging, setIsDragging] = useState(false);
    const [dragType, setDragType] = useState(null);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [dragCropStart, setDragCropStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [mediaDimensions, setMediaDimensions] = useState({ width: 0, height: 0 });
    const [isProcessing, setIsProcessing] = useState(false);
    const [layerKey, setLayerKey] = useState(null);

    // Refs
    const previewRef = useRef(null);
    const videoRef = useRef(null);
    const animFrameRef = useRef(null);
    const trimTrackRef = useRef(null);

    // ── Helpers ──────────────────────────────────────────────────────
    // Compute largest crop rect (in %) that fits the target aspect ratio inside the media
    const calcInitialCrop = useCallback((mediaW, mediaH, tW, tH) => {
        const targetAspect = tW / tH;
        const mediaAspect = mediaW / mediaH;

        let w, h, x, y;
        if (mediaAspect >= targetAspect) {
            // Media is wider → full height, constrain width
            h = 100;
            w = (targetAspect / mediaAspect) * 100;
            x = (100 - w) / 2;
            y = 0;
        } else {
            // Media is taller → full width, constrain height
            w = 100;
            h = (mediaAspect / targetAspect) * 100;
            x = 0;
            y = (100 - h) / 2;
        }
        return { x, y, width: w, height: h };
    }, []);

    // ── Imperative API ───────────────────────────────────────────────
    useImperativeHandle(ref, () => ({
        /**
         * @param {string}   type      'image' | 'video' | 'audio'
         * @param {string}   url       media URL
         * @param {Function} callback  receives result (string url or {url, cropData, ...})
         * @param {Object}   opts      { targetWidth, targetHeight, maxDuration }
         */
        open: (type, url, callback, opts = {}) => {
            const tW = opts.targetWidth || DEFAULT_TARGET_WIDTH;
            const tH = opts.targetHeight || DEFAULT_TARGET_HEIGHT;
            const maxDur = opts.maxDuration || DEFAULT_MAX_TRIM;

            setTargetWidth(tW);
            setTargetHeight(tH);
            setMaxTrimDuration(maxDur);
            setStockThumbnailUrl(opts.stockThumbnailUrl || null);
            setCrop({ x: 0, y: 0, width: 100, height: 100 });
            setTrimStart(0);
            setTrimEnd(maxDur);
            setIsPlaying(false);
            setCurrentTime(0);
            setVideoDuration(0);
            setMediaDimensions({ width: 0, height: 0 });
            setLayerKey(opts.layerKey || null);
            setMediaType(type || 'image');
            setMediaUrl(url);
            setOnConfirmCallback(() => callback);
            setIsOpen(true);
        },
        close: () => { setIsOpen(false); setIsPlaying(false); },
    }), []);

    // ── Video time loop ──────────────────────────────────────────────
    useEffect(() => {
        if (!isPlaying || !videoRef.current) return;
        const tick = () => {
            const vid = videoRef.current;
            if (!vid) return;
            setCurrentTime(vid.currentTime);
            if (vid.currentTime >= trimEnd) vid.currentTime = trimStart;
            animFrameRef.current = requestAnimationFrame(tick);
        };
        animFrameRef.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(animFrameRef.current);
    }, [isPlaying, trimStart, trimEnd]);

    // ── Close / Skip / Apply ─────────────────────────────────────────
    const handleClose = useCallback(() => { setIsOpen(false); setIsPlaying(false); }, []);

    const handleSkip = useCallback(() => {
        if (onConfirmCallback) {
            // When skipping, pass the stock thumbnail so callers can use it as a poster
            onConfirmCallback(stockThumbnailUrl
                ? { url: mediaUrl, thumbnailUrl: stockThumbnailUrl }
                : mediaUrl
            );
        }
        setIsOpen(false);
        setIsPlaying(false);
    }, [onConfirmCallback, mediaUrl, stockThumbnailUrl]);

    const handleApply = useCallback(async () => {
        // Only skip crop if the crop covers the entire media (no actual cropping).
        // When the media aspect ratio differs from the target, the initial crop is a
        // real crop that must be sent to the server — it should NOT be skipped.
        const isFullMedia =
            Math.abs(crop.x) < 0.5 &&
            Math.abs(crop.y) < 0.5 &&
            Math.abs(crop.width - 100) < 0.5 &&
            Math.abs(crop.height - 100) < 0.5;

        const cropData = isFullMedia ? null : { x: crop.x, y: crop.y, width: crop.width, height: crop.height };

        const isVideo = mediaType === 'video';

        // Only include trim data when the user actually changed the trim range
        // (i.e. not starting at 0 or not ending at the full video / maxTrimDuration cap)
        const effectiveEnd = Math.min(maxTrimDuration, videoDuration);
        const isTrimChanged = isVideo && (
            trimStart > 0.05 ||
            Math.abs(trimEnd - effectiveEnd) > 0.05
        );
        const trimData = isTrimChanged ? { startTime: trimStart, endTime: trimEnd, maxDuration: maxTrimDuration } : null;
        const hasEdits = cropData || trimData;

        if (hasEdits) {
            setIsProcessing(true);
            try {
                const response = await mediaAPI.processCropTrim({
                    url: mediaUrl,
                    cropData,
                    trimData,
                    originalDimensions: mediaDimensions.width > 0 ? { ...mediaDimensions } : null,
                    mediaType,
                    layerKey,
                });
                const resultData = response.data.data;
                // Pass an object with url + thumbnailUrl so callers can store the poster
                const thumb = resultData.thumbnailUrl || stockThumbnailUrl;
                onConfirmCallback?.(thumb
                    ? { url: resultData.url, thumbnailUrl: thumb }
                    : resultData.url
                );
            } catch (error) {
                console.error('Error processing media:', error);
                alert('Failed to process media. Using original instead.');
                // Still include the stock thumbnail on error fallback
                onConfirmCallback?.(stockThumbnailUrl
                    ? { url: mediaUrl, thumbnailUrl: stockThumbnailUrl }
                    : mediaUrl
                );
            } finally {
                setIsProcessing(false);
            }
        } else {
            // No edits — pass the stock thumbnail so callers can use it as a poster
            onConfirmCallback?.(stockThumbnailUrl
                ? { url: mediaUrl, thumbnailUrl: stockThumbnailUrl }
                : mediaUrl
            );
        }
        setIsOpen(false);
        setIsPlaying(false);
    }, [crop, trimStart, trimEnd, mediaUrl, mediaType, mediaDimensions, targetWidth, targetHeight, maxTrimDuration, videoDuration, onConfirmCallback, stockThumbnailUrl, layerKey]);

    // ── Media loaded ─────────────────────────────────────────────────
    const handleImageLoaded = useCallback((e) => {
        const dims = { width: e.target.naturalWidth, height: e.target.naturalHeight };
        setMediaDimensions(dims);
        setCrop(calcInitialCrop(dims.width, dims.height, targetWidth, targetHeight));
    }, [targetWidth, targetHeight, calcInitialCrop]);

    const handleVideoLoaded = useCallback((e) => {
        const vid = e.target;
        const dur = vid.duration || 0;
        setVideoDuration(dur);
        const dims = { width: vid.videoWidth, height: vid.videoHeight };
        setMediaDimensions(dims);
        setCrop(calcInitialCrop(dims.width, dims.height, targetWidth, targetHeight));
        setTrimEnd(Math.min(maxTrimDuration, dur));
        setTrimStart(0);
        setCurrentTime(0);
    }, [targetWidth, targetHeight, maxTrimDuration, calcInitialCrop]);

    const togglePlayPause = useCallback(() => {
        const vid = videoRef.current;
        if (!vid) return;
        if (isPlaying) { vid.pause(); setIsPlaying(false); }
        else { vid.currentTime = trimStart; vid.play().catch(() => { }); setIsPlaying(true); }
    }, [isPlaying, trimStart]);

    // ── Crop drag ────────────────────────────────────────────────────
    const getRelPos = useCallback((e) => {
        const rect = previewRef.current?.getBoundingClientRect();
        if (!rect) return { x: 0, y: 0 };
        return { x: ((e.clientX - rect.left) / rect.width) * 100, y: ((e.clientY - rect.top) / rect.height) * 100 };
    }, []);

    const startCropDrag = useCallback((e, type) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
        setDragType(type);
        setDragStart(getRelPos(e));
        setDragCropStart({ ...crop });
    }, [crop, getRelPos]);

    const onCropMouseMove = useCallback((e) => {
        if (!isDragging || mediaDimensions.width === 0) return;
        const pos = getRelPos(e);
        const dx = pos.x - dragStart.x;
        const dy = pos.y - dragStart.y;
        const aspectRatio = targetWidth / targetHeight;
        const mediaAspect = mediaDimensions.width / mediaDimensions.height;

        setCrop(() => {
            let { x, y, width, height } = dragCropStart;

            if (dragType === 'move') {
                x = clamp(x + dx, 0, 100 - width);
                y = clamp(y + dy, 0, 100 - height);
            } else {
                // Corner resize — maintain aspect ratio
                // Use the primary axis (dx for east/west, dy for north/south)
                let newWidth = width;
                let newHeight = height;

                if (dragType === 'se') {
                    newWidth = Math.max(MIN_CROP_PCT, width + dx);
                    newHeight = (newWidth / aspectRatio) * mediaAspect;
                } else if (dragType === 'sw') {
                    newWidth = Math.max(MIN_CROP_PCT, width - dx);
                    newHeight = (newWidth / aspectRatio) * mediaAspect;
                    x = x + width - newWidth;
                } else if (dragType === 'ne') {
                    newWidth = Math.max(MIN_CROP_PCT, width + dx);
                    newHeight = (newWidth / aspectRatio) * mediaAspect;
                    y = y + height - newHeight;
                } else if (dragType === 'nw') {
                    newWidth = Math.max(MIN_CROP_PCT, width - dx);
                    newHeight = (newWidth / aspectRatio) * mediaAspect;
                    x = x + width - newWidth;
                    y = y + height - newHeight;
                }

                // Clamp to bounds
                if (x < 0) { newWidth += x; newHeight = (newWidth / aspectRatio) * mediaAspect; x = 0; }
                if (y < 0) { newHeight += y; newWidth = (newHeight * aspectRatio) / mediaAspect; y = 0; }
                if (x + newWidth > 100) { newWidth = 100 - x; newHeight = (newWidth / aspectRatio) * mediaAspect; }
                if (y + newHeight > 100) { newHeight = 100 - y; newWidth = (newHeight * aspectRatio) / mediaAspect; }

                width = Math.max(MIN_CROP_PCT, newWidth);
                height = Math.max(MIN_CROP_PCT, newHeight);
            }

            return { x, y, width, height };
        });
    }, [isDragging, dragType, dragStart, dragCropStart, getRelPos, mediaDimensions, targetWidth, targetHeight]);

    const onCropMouseUp = useCallback(() => { setIsDragging(false); setDragType(null); }, []);

    // Global mouse listeners for drag
    useEffect(() => {
        if (!isDragging) return;
        window.addEventListener('mousemove', onCropMouseMove);
        window.addEventListener('mouseup', onCropMouseUp);
        return () => {
            window.removeEventListener('mousemove', onCropMouseMove);
            window.removeEventListener('mouseup', onCropMouseUp);
        };
    }, [isDragging, onCropMouseMove, onCropMouseUp]);

    // ── Trim drag ────────────────────────────────────────────────────
    const handleTrimDrag = useCallback((e, handle) => {
        e.preventDefault();
        const track = trimTrackRef.current;
        if (!track || videoDuration <= 0) return;

        const currentDur = trimEnd - trimStart;
        const startX = e.clientX;
        const initialStart = trimStart;
        const initialEnd = trimEnd;

        const onMove = (moveEvent) => {
            const rect = track.getBoundingClientRect();
            const ratio = clamp((moveEvent.clientX - rect.left) / rect.width, 0, 1);
            const time = ratio * videoDuration;

            if (handle === 'start') {
                let ns = clamp(time, 0, trimEnd - 0.1);
                if (trimEnd - ns > maxTrimDuration) ns = trimEnd - maxTrimDuration;
                setTrimStart(ns);
            } else if (handle === 'end') {
                let ne = clamp(time, trimStart + 0.1, videoDuration);
                if (ne - trimStart > maxTrimDuration) ne = trimStart + maxTrimDuration;
                setTrimEnd(ne);
            } else if (handle === 'move') {
                const deltaTime = ((moveEvent.clientX - startX) / rect.width) * videoDuration;
                let ns = initialStart + deltaTime;
                let ne = initialEnd + deltaTime;
                if (ns < 0) { ns = 0; ne = currentDur; }
                if (ne > videoDuration) { ne = videoDuration; ns = videoDuration - currentDur; }
                setTrimStart(ns);
                setTrimEnd(ne);
            }
        };

        const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    }, [trimStart, trimEnd, videoDuration, maxTrimDuration]);

    // ── Early return ─────────────────────────────────────────────────
    if (!isOpen) return null;

    const isVideo = mediaType === 'video';
    const trimDuration = (trimEnd - trimStart).toFixed(1);
    const aspectRatio = targetWidth / targetHeight;

    // Calculate display dimensions for the crop area
    let displayW = targetWidth;
    let displayH = targetHeight;
    if (mediaDimensions.width > 0) {
        const srcW = mediaDimensions.width * crop.width / 100;
        const srcH = mediaDimensions.height * crop.height / 100;
        // Scale proportionally to target
        const scaleX = srcW / targetWidth;
        const scaleY = srcH / targetHeight;
        const scale = Math.min(scaleX, scaleY);
        displayW = Math.round(targetWidth * scale);
        displayH = Math.round(targetHeight * scale);
    }

    // ── Render ───────────────────────────────────────────────────────
    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-70">
            <div className="bg-ag-black-800 rounded-lg shadow-2xl w-[720px] max-h-[90vh] flex flex-col border border-ag-black-600">

                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-ag-black-600">
                    <div className="flex items-center gap-2">
                        <button onClick={handleClose} className="text-gray-400 hover:text-white transition-colors p-1 rounded hover:bg-ag-black-700" title="Back">
                            <ArrowLeft className="w-4 h-4" />
                        </button>
                        <h2 className="text-base font-semibold text-white flex items-center gap-2">
                            {isVideo ? <Scissors className="w-4 h-4 text-ag-green" /> : <Crop className="w-4 h-4 text-ag-green" />}
                            {isVideo ? 'Crop & Trim Video' : 'Crop Image'}
                        </h2>
                    </div>
                    <button onClick={handleClose} className="text-gray-400 hover:text-white transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Preview Area */}
                <div className="flex-1 overflow-hidden p-4 flex flex-col items-center justify-center">
                    <div
                        ref={previewRef}
                        className="relative bg-black rounded-lg overflow-hidden select-none"
                        style={{
                            height: isVideo ? '340px' : '420px',
                            maxWidth: '100%',
                            aspectRatio: mediaDimensions.width > 0 ? `${mediaDimensions.width} / ${mediaDimensions.height}` : 'auto'
                        }}
                    >
                        {/* Media element — static, no zoom */}
                        {isVideo ? (
                            <video
                                crossOrigin='anonymous'
                                ref={videoRef}
                                src={mediaUrl}
                                className="absolute inset-0 w-full h-full object-cover"
                                onLoadedMetadata={handleVideoLoaded}
                                muted
                            />
                        ) : (
                            <img
                                crossOrigin='anonymous'
                                src={mediaUrl}
                                alt="Crop preview"
                                className="absolute inset-0 w-full h-full object-cover"
                                onLoad={handleImageLoaded}
                                draggable={false}
                            />
                        )}

                        {/* Dark overlay around crop area */}
                        <div className="absolute inset-0 pointer-events-none">
                            <div className="absolute bg-black bg-opacity-60" style={{ top: 0, left: 0, right: 0, height: `${crop.y}%` }} />
                            <div className="absolute bg-black bg-opacity-60" style={{ bottom: 0, left: 0, right: 0, height: `${100 - crop.y - crop.height}%` }} />
                            <div className="absolute bg-black bg-opacity-60" style={{ top: `${crop.y}%`, left: 0, width: `${crop.x}%`, height: `${crop.height}%` }} />
                            <div className="absolute bg-black bg-opacity-60" style={{ top: `${crop.y}%`, right: 0, width: `${100 - crop.x - crop.width}%`, height: `${crop.height}%` }} />
                        </div>

                        {/* Crop rectangle */}
                        <div
                            className="absolute border-2 border-ag-green cursor-move"
                            style={{ left: `${crop.x}%`, top: `${crop.y}%`, width: `${crop.width}%`, height: `${crop.height}%` }}
                            onMouseDown={(e) => startCropDrag(e, 'move')}
                        >
                            {/* Rule-of-thirds grid */}
                            <div className="absolute inset-0 pointer-events-none">
                                <div className="absolute left-1/3 top-0 bottom-0 w-px bg-ag-green opacity-30" />
                                <div className="absolute left-2/3 top-0 bottom-0 w-px bg-ag-green opacity-30" />
                                <div className="absolute top-1/3 left-0 right-0 h-px bg-ag-green opacity-30" />
                                <div className="absolute top-2/3 left-0 right-0 h-px bg-ag-green opacity-30" />
                            </div>

                            {/* Corner handles */}
                            {['nw', 'ne', 'sw', 'se'].map((corner) => (
                                <div
                                    key={corner}
                                    className="absolute w-3 h-3 bg-ag-green rounded-sm"
                                    style={{
                                        ...(corner.includes('n') ? { top: -6 } : { bottom: -6 }),
                                        ...(corner.includes('w') ? { left: -6 } : { right: -6 }),
                                        cursor: corner === 'nw' || corner === 'se' ? 'nwse-resize' : 'nesw-resize',
                                    }}
                                    onMouseDown={(e) => startCropDrag(e, corner)}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Crop info */}
                    <div className="flex items-center justify-center gap-4 mt-2">
                        <span className="text-xs text-gray-400">
                            Output: {displayW} × {displayH} px  →  Target: {targetWidth}×{targetHeight}
                        </span>
                        <button
                            onClick={() => {
                                if (mediaDimensions.width > 0) {
                                    setCrop(calcInitialCrop(mediaDimensions.width, mediaDimensions.height, targetWidth, targetHeight));
                                }
                            }}
                            className="text-xs text-ag-green hover:text-green-400 transition-colors"
                        >
                            Reset crop
                        </button>
                    </div>
                </div>

                {/* Trim Controls (video only) */}
                {isVideo && (
                    <div className="px-5 pb-3 border-t border-ag-black-600 pt-3">
                        <div className="flex items-center gap-3 mb-2">
                            <button onClick={togglePlayPause} className="p-1.5 rounded bg-ag-black-700 hover:bg-ag-black-600 text-white transition-colors">
                                {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                            </button>
                            <span className="text-xs text-gray-400 font-mono">
                                {formatTime(currentTime)} / {formatTime(videoDuration)}
                            </span>
                            <div className="flex-1" />
                            <span className="text-xs text-white bg-ag-black-700 px-2 py-0.5 rounded">
                                Trim: {trimDuration}s
                            </span>
                            <span className="text-xs text-gray-500">(max {maxTrimDuration}s)</span>
                        </div>

                        {/* Trim timeline */}
                        <div className="relative h-10 select-none" ref={trimTrackRef}>
                            <div className="absolute top-3 left-0 right-0 h-4 bg-ag-black-900 rounded" />

                            {/* Selected range */}
                            <div
                                className="absolute top-3 h-4 bg-ag-green bg-opacity-30 rounded cursor-grab active:cursor-grabbing hover:bg-opacity-40 flex items-center justify-center group"
                                style={{
                                    left: `${(trimStart / videoDuration) * 100}%`,
                                    width: `${((trimEnd - trimStart) / videoDuration) * 100}%`,
                                }}
                                onMouseDown={(e) => handleTrimDrag(e, 'move')}
                                title="Drag to move trim window"
                            >
                                <Hand className="w-3 h-3 text-ag-green opacity-0 group-hover:opacity-70 transition-opacity pointer-events-none" />
                            </div>

                            {/* Current time */}
                            <div className="absolute top-2 w-0.5 h-6 bg-white rounded pointer-events-none" style={{ left: `${(currentTime / videoDuration) * 100}%` }} />

                            {/* Start handle */}
                            <div
                                className="absolute top-1 w-3 h-8 bg-ag-green rounded cursor-ew-resize flex items-center justify-center z-10 hover:bg-green-400 transition-colors"
                                style={{ left: `calc(${(trimStart / videoDuration) * 100}% - 6px)` }}
                                onMouseDown={(e) => handleTrimDrag(e, 'start')}
                            >
                                <div className="w-0.5 h-3 bg-black rounded" />
                            </div>

                            {/* End handle */}
                            <div
                                className="absolute top-1 w-3 h-8 bg-ag-green rounded cursor-ew-resize flex items-center justify-center z-10 hover:bg-green-400 transition-colors"
                                style={{ left: `calc(${(trimEnd / videoDuration) * 100}% - 6px)` }}
                                onMouseDown={(e) => handleTrimDrag(e, 'end')}
                            >
                                <div className="w-0.5 h-3 bg-black rounded" />
                            </div>

                            {/* Time labels */}
                            <div className="absolute top-[30px] left-0 text-[10px] text-gray-500 font-mono">{formatTime(trimStart)}</div>
                            <div className="absolute top-[30px] right-0 text-[10px] text-gray-500 font-mono">{formatTime(trimEnd)}</div>
                        </div>
                    </div>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between px-5 py-3 border-t border-ag-black-600">
                    <button onClick={handleSkip} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-400 hover:text-white transition-colors rounded hover:bg-ag-black-700">
                        <SkipForward className="w-3.5 h-3.5" />
                        Skip
                    </button>
                    <button
                        onClick={handleApply}
                        disabled={isProcessing}
                        className="flex items-center gap-1.5 px-5 py-1.5 text-sm font-medium text-black bg-ag-green hover:bg-green-400 transition-colors rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isProcessing ? (
                            <><Loader2 className="w-3.5 h-3.5 animate-spin" />Processing...</>
                        ) : (
                            <><Check className="w-3.5 h-3.5" />Apply</>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
});

// ── Utilities ────────────────────────────────────────────────────────
function formatTime(s) {
    if (!s || isNaN(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
}

function clamp(v, min, max) { return Math.min(max, Math.max(min, v)); }

MediaCropTrimModal.displayName = 'MediaCropTrimModal';
export default MediaCropTrimModal;
